/**
 * カイ二乗分布の上側確率。
 * サンプル比率の乖離 (SRM) 検定で自由度が 1 以外になるため、
 * 不完全ガンマ関数を実装している。
 */

const LANCZOS = [
  76.18009172947146, -86.50532032941677, 24.01409824083091,
  -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5,
]

/** log Γ(x) の Lanczos 近似 (x > 0)。 */
export function logGamma(x: number): number {
  let y = x
  const tmp = x + 5.5
  const t = tmp - (x + 0.5) * Math.log(tmp)
  let ser = 1.000000000190015
  for (let j = 0; j < 6; j++) {
    y += 1
    ser += LANCZOS[j] / y
  }
  return -t + Math.log((2.5066282746310005 * ser) / x)
}

const ITMAX = 300
const EPS = 3e-16
const FPMIN = Number.MIN_VALUE / EPS

/** 正規化下側不完全ガンマ関数 P(a, x) を級数展開で求める (x < a + 1 向け)。 */
function gammaSeries(a: number, x: number): number {
  const gln = logGamma(a)
  let ap = a
  let sum = 1 / a
  let del = sum
  for (let n = 0; n < ITMAX; n++) {
    ap += 1
    del *= x / ap
    sum += del
    if (Math.abs(del) < Math.abs(sum) * EPS) break
  }
  return sum * Math.exp(-x + a * Math.log(x) - gln)
}

/** 正規化上側不完全ガンマ関数 Q(a, x) を連分数展開で求める (x >= a + 1 向け)。 */
function gammaContinuedFraction(a: number, x: number): number {
  const gln = logGamma(a)
  let b = x + 1 - a
  let c = 1 / FPMIN
  let d = 1 / b
  let h = d
  for (let i = 1; i <= ITMAX; i++) {
    const an = -i * (i - a)
    b += 2
    d = an * d + b
    if (Math.abs(d) < FPMIN) d = FPMIN
    c = b + an / c
    if (Math.abs(c) < FPMIN) c = FPMIN
    d = 1 / d
    const del = d * c
    h *= del
    if (Math.abs(del - 1) < EPS) break
  }
  return Math.exp(-x + a * Math.log(x) - gln) * h
}

/** 正規化下側不完全ガンマ関数 P(a, x)。 */
export function gammaP(a: number, x: number): number {
  if (x <= 0 || a <= 0) return 0
  return x < a + 1 ? gammaSeries(a, x) : 1 - gammaContinuedFraction(a, x)
}

/** 正規化上側不完全ガンマ関数 Q(a, x) = 1 - P(a, x)。 */
export function gammaQ(a: number, x: number): number {
  if (x <= 0 || a <= 0) return 1
  return x < a + 1 ? 1 - gammaSeries(a, x) : gammaContinuedFraction(a, x)
}

/** 自由度 df のカイ二乗分布の上側確率 P(X > x)。 */
export function chiSquareSf(x: number, df: number): number {
  if (!Number.isFinite(x) || x <= 0) return 1
  if (df <= 0) return NaN
  return gammaQ(df / 2, x / 2)
}
